/**
 * The simple gas laws and the combined gas law as one calculator.
 *
 * Every law says that a group of the variables P, V, T and n has the same value in two
 * states of one gas sample, so each is described by the exponent (+1 or −1) of every
 * variable in that group:
 *
 *   combined    P·V / T      Boyle      P·V       Charles    V / T
 *   Gay-Lussac  P / T        Avogadro   V / n
 *
 * Because the equation is a ratio, pressure, volume and amount may stay in whatever unit
 * the reader typed — it cancels — so long as both states share it. Temperature is the
 * exception: it is converted to kelvin first and must be above absolute zero.
 */

import { parseMeasuredNumber } from "@/lib/tool-kit/math/number-input";
import { type GasTerm, gasUnitSymbol } from "./gas";
import { fromKelvin, toKelvin } from "./units";

export type GasLaw =
  | "combined"
  | "boyle"
  | "charles"
  | "gayLussac"
  | "avogadro";
export type StateNumber = 1 | 2;

export interface LawTerm {
  term: GasTerm;
  exponent: 1 | -1;
}

export const GAS_LAWS: Record<GasLaw, readonly LawTerm[]> = {
  combined: [
    { term: "p", exponent: 1 },
    { term: "v", exponent: 1 },
    { term: "t", exponent: -1 },
  ],
  boyle: [
    { term: "p", exponent: 1 },
    { term: "v", exponent: 1 },
  ],
  charles: [
    { term: "v", exponent: 1 },
    { term: "t", exponent: -1 },
  ],
  gayLussac: [
    { term: "p", exponent: 1 },
    { term: "t", exponent: -1 },
  ],
  avogadro: [
    { term: "v", exponent: 1 },
    { term: "n", exponent: -1 },
  ],
};

export const GAS_LAW_IDS = Object.keys(GAS_LAWS) as GasLaw[];

export interface UnknownSpec {
  term: GasTerm;
  state: StateNumber;
}

/** "p1", "v2" … the key of one entry. */
export function entryKey(term: GasTerm, state: StateNumber): string {
  return `${term}${state}`;
}

const SUBSCRIPTS = { 1: "₁", 2: "₂" } as const;
const SYMBOLS: Record<GasTerm, string> = { p: "P", v: "V", t: "T", n: "n" };

/** "P₁", "T₂" */
export function entrySymbol(term: GasTerm, state: StateNumber): string {
  return `${SYMBOLS[term]}${SUBSCRIPTS[state]}`;
}

export function otherState(state: StateNumber): StateNumber {
  return state === 1 ? 2 : 1;
}

/** The law's equation as text: "P₁V₁ ÷ T₁ = P₂V₂ ÷ T₂". */
export function lawEquation(law: GasLaw): string {
  const side = (state: StateNumber) => {
    const terms = GAS_LAWS[law];
    const top = terms
      .filter((t) => t.exponent === 1)
      .map((t) => entrySymbol(t.term, state))
      .join("");
    const bottom = terms
      .filter((t) => t.exponent === -1)
      .map((t) => entrySymbol(t.term, state))
      .join("");
    return bottom === "" ? top : `${top} ÷ ${bottom}`;
  };
  return `${side(1)} = ${side(2)}`;
}

export interface Factor {
  term: GasTerm;
  state: StateNumber;
  /** +1 multiplies the unknown's value by this entry, −1 divides it. */
  power: 1 | -1;
}

/**
 * The unknown as a product of the other entries, each to the power ±1. From
 * Q₁ = Q₂ with Q = Π vᵉ, the unknown in state s gets (e_v/e_u) for each term v in the
 * other state and −(e_v/e_u) for each of the remaining terms in its own state.
 */
export function rearrange(law: GasLaw, unknown: UnknownSpec): Factor[] {
  const terms = GAS_LAWS[law];
  const own = terms.find((t) => t.term === unknown.term)?.exponent ?? 1;
  const factors: Factor[] = [];
  for (const state of [1, 2] as const) {
    for (const t of terms) {
      if (state === unknown.state && t.term === unknown.term) {
        continue;
      }
      const sign = state === unknown.state ? -1 : 1;
      factors.push({
        term: t.term,
        state,
        power: (sign * t.exponent * own) as 1 | -1,
      });
    }
  }
  return factors;
}

/** "V₂ = (P₁ × V₁ × T₂) ÷ (T₁ × P₂)" */
export function rearrangedText(law: GasLaw, unknown: UnknownSpec): string {
  const factors = rearrange(law, unknown);
  const top = factors.filter((f) => f.power === 1);
  const bottom = factors.filter((f) => f.power === -1);
  const join = (list: Factor[]) => {
    const text = list.map((f) => entrySymbol(f.term, f.state)).join(" × ");
    return list.length > 1 ? `(${text})` : text;
  };
  const numerator = top.length === 0 ? "1" : join(top);
  const right =
    bottom.length === 0 ? numerator : `${numerator} ÷ ${join(bottom)}`;
  return `${entrySymbol(unknown.term, unknown.state)} = ${right}`;
}

/* ----------------------------------------------------------- the solve */

export interface GasLawInput {
  law: GasLaw;
  unknown: UnknownSpec;
  /** Raw text by `entryKey`. */
  text: Record<string, string>;
  /** One unit per variable, shared by both states. */
  unit: Record<GasTerm, string>;
}

export type GasLawFailure =
  | { ok: false; reason: "missing" }
  | {
      ok: false;
      reason: "unreadable" | "nonPositive" | "belowZero";
      term: GasTerm;
      state: StateNumber;
    };

export interface GasLawSolution {
  ok: true;
  law: GasLaw;
  unknown: UnknownSpec;
  /** Every entry in its working unit: the typed unit for P, V, n and kelvin for T. */
  values: Record<string, number>;
  /** The unknown in its working unit. */
  working: number;
  /** The unknown in the unit its menu chose. */
  answer: number;
  answerUnit: string;
  /** The unknown divided by the same variable in the other state. */
  ratio: number;
}

export type GasLawOutcome = GasLawFailure | GasLawSolution;

function readEntry(
  term: GasTerm,
  state: StateNumber,
  text: string,
  unit: string
): { value: number } | GasLawFailure {
  if (text.trim() === "") {
    return { ok: false, reason: "missing" };
  }
  const typed = parseMeasuredNumber(text);
  if (typed === null) {
    return { ok: false, reason: "unreadable", term, state };
  }
  if (term === "t") {
    const kelvin = toKelvin(typed, unit);
    return kelvin > 0
      ? { value: kelvin }
      : { ok: false, reason: "belowZero", term, state };
  }
  return typed > 0
    ? { value: typed }
    : { ok: false, reason: "nonPositive", term, state };
}

export function solveGasLaw(input: GasLawInput): GasLawOutcome {
  const { law, unknown } = input;
  const values: Record<string, number> = {};
  let missing = false;
  for (const { term } of GAS_LAWS[law]) {
    for (const state of [1, 2] as const) {
      if (term === unknown.term && state === unknown.state) {
        continue;
      }
      const key = entryKey(term, state);
      const read = readEntry(
        term,
        state,
        input.text[key] ?? "",
        input.unit[term]
      );
      if ("ok" in read) {
        if (read.reason !== "missing") {
          return read;
        }
        missing = true;
      } else {
        values[key] = read.value;
      }
    }
  }
  if (missing) {
    return { ok: false, reason: "missing" };
  }
  let working = 1;
  for (const f of rearrange(law, unknown)) {
    working *= values[entryKey(f.term, f.state)] ** f.power;
  }
  values[entryKey(unknown.term, unknown.state)] = working;
  const other = values[entryKey(unknown.term, otherState(unknown.state))];
  const unit = input.unit[unknown.term];
  return {
    ok: true,
    law,
    unknown,
    values,
    working,
    answer: unknown.term === "t" ? fromKelvin(working, unit) : working,
    answerUnit: unit,
    ratio: working / other,
  };
}

/** Symbol of the unit a working value is in (kelvin for T). */
export function workingUnitSymbol(term: GasTerm, unit: string): string {
  return term === "t" ? "K" : gasUnitSymbol(term, unit);
}
